import * as React from "react"
import { Box, Skeleton, Typography } from "@mui/material"
import { BasicTableSeeker } from "@ui-seeker/core"
import { capitalize } from "@ui-seeker/utils"

import { NotAllowedBlock } from "@components"
import {BORDER_COLOR, BORDER_STYLE, DIGITAL_SHELF_KPI_HELP, USER_ROLES} from "@constants"
import {useToast, useUserApp} from "@hooks"
import { isLoadingCommonFilters } from "@signals"
import { useDigitalShelfTableData } from "./@hooks";
import { useDigitalShelfRoleConfig } from "../@hooks";
import { valueColumn, visionSplitColumn } from "./columns"
import DigitalShelfTableCustomHeadLabel, { DigitalShelfTableHeadLabelWithHelp } from "./CustomHeadLabel"
import DigitalShelfTableTitle from "./Title"
import { normalizeLabel } from "../utils"


export default function DigitalShelfTable() {

	const { userRole } = useUserApp()
	const { showToast } = useToast()
	const { data, summaryData, aggregations, digitalShelfChangeResults, handleChangeResults } = useDigitalShelfTableData()
	// Every url of this screen comes from the role hook — it is where the v2 migration is recorded.
	const { urlApiExcel } = useDigitalShelfRoleConfig()

	const NoDataMessage = () => <Typography align="center">
        No data matching the query was found.
    </Typography>

	if (isLoadingCommonFilters.value) return <Skeleton variant="rectangular" height={600} />
	if (!data.length) return <NoDataMessage />

	const kpis = [
		{ key: 'average', label: 'Digital Shelf Score', borderLeft: true },
		{ key: 'stock_kpi', label: 'Stock Score' },
		{ key: 'assortment_kpi', label: 'Assortment Score' },
		{ key: 'pp_kpi', label: 'Product Page Score' },
		{ key: 'sos_kpi', label: 'Share of Search Score', restricted: userRole === USER_ROLES.MARKETPLACES },
		{ key: 'sos_organic_kpi', label: 'Share of Search Organic Score', restricted: userRole === USER_ROLES.MARKETPLACES },
		{ key: 'sos_sponsored_kpi', label: 'Share of Search Sponsored Score', restricted: userRole === USER_ROLES.MARKETPLACES }
	]

	const kpiHeaders = kpis.map(kpi => {
		const help = DIGITAL_SHELF_KPI_HELP[kpi.key]

		if (kpi.noData || kpi.restricted) {
			return { render: () => <>
				<NotAllowedBlock symbol="N/A"/>
				<DigitalShelfTableHeadLabelWithHelp label={kpi.label} help={help} />
			</> }
		}

		const value = summaryData?.[kpi.key]
		const prev = summaryData?.[`${kpi.key}Prev`]
		const diff = summaryData?.[`${kpi.key}Diff`]

		return {
			sx: kpi.borderLeft ? { borderLeft: BORDER_STYLE, borderColor: BORDER_COLOR } : {},
			render: () => <DigitalShelfTableCustomHeadLabel
				label={kpi.label}
				value={value}
				prev={prev}
				diff={diff}
				showDiff={digitalShelfChangeResults}
				help={help}
			/>
		}
	})

	return <BasicTableSeeker
		data={data}
		title={<DigitalShelfTableTitle showDiff={digitalShelfChangeResults} onChange={handleChangeResults} />}
		setHeaderCellProps={() => ({
			sx: { borderBottom: BORDER_STYLE, borderColor: BORDER_COLOR }
		})}
		headers={[[
			...aggregations.map(label => ({ label: capitalize(normalizeLabel(label)) })),
			...kpiHeaders
		]]}
		columns={[
			...aggregations.map(label => visionSplitColumn(label)),
			valueColumn('average', '', digitalShelfChangeResults, false),
			valueColumn('stock_kpi', 'stockAssortment', digitalShelfChangeResults, true, 'Go to Stock & Assortment Details'),
			valueColumn('assortment_kpi', 'stockAssortment', digitalShelfChangeResults, true, 'Go to Stock & Assortment Details'),
			valueColumn('pp_kpi', 'productPages', digitalShelfChangeResults, true, 'Go to Product Page KPI'),
			valueColumn('sos_kpi', 'shareOfsearch', digitalShelfChangeResults, true, 'Go to Share Of Search KPI'),
			valueColumn('sos_organic_kpi', 'shareOfsearch', digitalShelfChangeResults, true, 'Go to Share Of Search KPI'),
			valueColumn('sos_sponsored_kpi', 'shareOfsearch', digitalShelfChangeResults, true, 'Go to Share Of Search KPI')
		]}
		onDownload={() => showToast.promiseExcel(urlApiExcel)}
	/>
}